type UserQuery = Record<string, string>

type UrlObject = {
  app: string
  port: string
  schema: string
  server: string
  stream: string
  url: String
  vhost: string
  user_query?: UserQuery
  [key: keyof UserQuery]: any
}

type WebRTCPlayResponse = {
  code: number
  sdp: string
  server: string
  sessionid: string
}

export class SrsRtcPlayer {
  
  private _defaultPath = '/rtc/v1/play/'
  private _pc: RTCPeerConnection | null
  private _stream: MediaStream

  constructor() {
    this._pc = new RTCPeerConnection()
    this._pc.ontrack = (event: RTCTrackEvent) => {
      this.onTrack(event)
    }
    
    this._stream = new MediaStream()
  }

  // Internal APIs.
  private _prepareUrl(webrtcUrl: string) {
    const urlObject = this._parse(webrtcUrl)

    // If user specifies the schema, use it as API schema.
    let schema = urlObject.user_query?.schema
    schema = schema ? schema + ':' : window.location.protocol

    let port = urlObject.port || '1985'
    if (schema === 'https:') {
      port = urlObject.port || '443'
    }

    // @see https://github.com/rtcdn/rtcdn-draft
    let api = urlObject.user_query?.play || this._defaultPath
    if (api.lastIndexOf('/') !== api.length - 1) {
      api += '/'
    }

    let apiUrl = schema + '//' + urlObject.server + ':' + port + api
    for (var key in urlObject.user_query) {
      if (key !== 'api' && key !== 'play') {
        apiUrl += '&' + key + '=' + urlObject.user_query[key]
      }
    }
    // Replace /rtc/v1/play/&k=v to /rtc/v1/play/?k=v
    apiUrl = apiUrl.replace(api + '&', api + '?')

    let streamUrl = urlObject.url

    return {
      apiUrl,
      streamUrl,
      schema,
      urlObject,
      port,
      tid: (new Date().getTime() * Math.random() * 100).toString(16).substr(0, 7),
    }
  }

  private _parse(url: string) {
    // @see: http://stackoverflow.com/questions/10469575/how-to-use-location-object-to-parse-url-without-redirecting-the-page-in-javascri
    const a = document.createElement('a')
    a.href = url.replace('rtmp://', 'http://').replace('webrtc://', 'http://').replace('rtc://', 'http://')

    let vhost = a.hostname
    let app = a.pathname.substring(1, a.pathname.lastIndexOf('/'))
    let stream = a.pathname.substring(a.pathname.lastIndexOf('/') + 1)

    // parse the vhost in the params of app, that srs supports.
    app = app.replace('...vhost...', '?vhost=')
    if (app.indexOf('?') >= 0) {
      var params = app.substring(app.indexOf('?'))
      app = app.substring(0, app.indexOf('?'))

      if (params.indexOf('vhost=') > 0) {
        vhost = params.substring(params.indexOf('vhost=') + 'vhost='.length)
        if (vhost.indexOf('&') > 0) {
          vhost = vhost.substring(0, vhost.indexOf('&'))
        }
      }
    }

    // when vhost equals to server, and server is ip,
    // the vhost is __defaultVhost__
    if (a.hostname === vhost) {
      const re = /^(\d+)\.(\d+)\.(\d+)\.(\d+)$/
      if (re.test(a.hostname)) {
        vhost = '__defaultVhost__'
      }
    }

    // parse the schema
    let schema = 'rtmp'
    if (url.indexOf('://') > 0) {
      schema = url.substring(0, url.indexOf('://'))
    }

    let port = a.port
    if (!port) {
      if (schema === 'http') {
        port = '80'
      } else if (schema === 'https') {
        port = '443'
      } else if (schema === 'rtmp') {
        port = '1935'
      }
    }

    const ret: UrlObject = {
      url,
      schema,
      server: a.hostname,
      port,
      vhost,
      app,
      stream,
    }
    this._fillQuery(a.search, ret)

    // For webrtc API, we use 443 if page is https, or schema specified it.
    if (!ret.port) {
      if (schema === 'webrtc' || schema === 'rtc') {
        if (ret.user_query?.schema === 'https') {
          ret.port = '443'
        } else if (window.location.href.indexOf('https://') === 0) {
          ret.port = '443'
        } else {
          // For WebRTC, SRS use 1985 as default API port.
          ret.port = '1985'
        }
      }
    }

    return ret
  }

  private _fillQuery(query_string: string, obj: UrlObject) {
    // pure user query object.
    obj.user_query = obj.user_query || {}

    if (query_string.length === 0) {
      return
    }

    // split again for angularjs.
    if (query_string.indexOf('?') >= 0) {
      query_string = query_string.split('?')[1]
    }

    const queries = query_string.split('&')
    for (var i = 0; i < queries.length; i++) {
      let elem = queries[i]

      let query = elem.split('=')
      obj[query[0]] = query[1]
      obj.user_query[query[0]] = query[1]
    }

    // alias domain for vhost.
    if (obj.domain) {
      obj.vhost = obj.domain
    }
  }

  async play(url: string) {
    if (!this._pc) {
      throw new Error('There is no RTCPeerConnection instance configured')
    }

    const conf = this._prepareUrl(url)
    console.info('self.pc', this._pc)
    this._pc.addTransceiver('audio', { direction: 'recvonly' })
    this._pc.addTransceiver('video', { direction: 'recvonly' })

    const offer = await this._pc.createOffer()
    await this._pc.setLocalDescription(offer)

    try {
      // @see https://github.com/rtcdn/rtcdn-draft
      const data = {
        api: conf.apiUrl,
        tid: conf.tid,
        streamurl: conf.streamUrl,
        clientip: null,
        sdp: offer.sdp,
      }
      console.info('Generated offer: ', data)
      console.info(conf.apiUrl)

      const response = await fetch(conf.apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      const answer: WebRTCPlayResponse = await response.json()
      console.info('Got answer: ', answer)

      await this._pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: answer.sdp }))
      const session = {
        ...answer,
        simulator: conf.schema + '//' + conf.urlObject.server + ':' + conf.port + '/rtc/v1/nack/'
      }

      return session
    } catch (err) {
      console.error(err)
    }
  }

  close() {
    if (this._pc) this._pc.close()
    this._pc = null
  }

  // The callback when got remote track.
  // Note that the onaddstream is deprecated, @see https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/onaddstream
  onTrack(event: RTCTrackEvent) {
    // https://webrtc.org/getting-started/remote-streams
    this._stream.addTrack(event.track)
  }

  getStream(): MediaStream {
    return this._stream
  }
}
